import React from 'react';
import {
  Flex,
  Button,
  MorphButton,
  Menu,
  MenuItem,
  MenuTrigger,
  SplitButton,
  TagBadge,
  Text,
  Tooltip,
  TooltipTrigger,
  type HintCardItem,
} from '@chatsdet/ui';
import {
  ArrowCircleUp,
  Gear,
  GitFork,
  Lightbulb,
  PauseCircle,
  PlayCircle,
  PushPin,
  PushPinSlash,
  Sliders,
  Sparkle,
} from '@phosphor-icons/react';
import type { SandboxEnvironmentState } from '@chatsdet/types';
import { DegradedHealthMenu } from '../DegradedHealthMenu';
import { DEFAULT_WORKFLOW_NAME } from '../constants';
import type {
  LifecycleButtonStateConfig,
  LifecycleState,
  RunState,
} from '../hooks/useLifecycleControls';
import {
  SuggestModeActions,
  SuggestModeIndicator,
  SuggestModeToggle,
} from '../proposals/SuggestModeControls';
import {
  ForkMergeDialog,
  ForkOriginLink,
  getForkLineageAction,
  isForkMergeDisabled,
  type WorkflowForkLineage,
} from './ForkLineageControls';
import { SandboxToolbarButton } from './SandboxToolbarButton';
import { SandboxV2ToolbarButton, type SandboxV2CurrentRun } from './SandboxV2ToolbarButton';

export type FlowDesignerToolbarProps = {
  workflowId: string | null;
  workflowCanvasTitle: string;
  workflowFlowType: 'standard' | 'setup' | 'seed' | 'teardown';
  generatedName: string | null;
  typingComplete: boolean;
  isWorkflowLoading: boolean;
  isSettingsOpen: boolean;
  setIsSettingsOpen: (v: boolean) => void;
  /** v1 (template-backed) sandbox environments. Mutually exclusive with `sandboxV2Control`. */
  sandboxControl?: {
    environmentName: string;
    sandbox: SandboxEnvironmentState;
    isEnsuring: boolean;
    isStopping: boolean;
    onStart: () => void;
    onStop: () => void;
  } | null;
  /** Sandboxes v2-backed run target. Mutually exclusive with `sandboxControl`. */
  sandboxV2Control?: {
    environmentName: string;
    environment: string | null;
    isLoading: boolean;
    status: 'ready' | 'warming' | 'stale' | 'none' | null;
    version: string | null;
    error: string | null;
    /** The active/most recent run's claimed copy, if any. */
    currentRun: SandboxV2CurrentRun;
  } | null;
  workflowHealth?: string;
  onReEnable?: () => void;
  isReEnabling?: boolean;
  hintData: {
    hints: HintCardItem[];
    isOpen: boolean;
    initialIndex: number;
    hasUnread: boolean;
    open: () => void;
    close: () => void;
    dismiss: (type: 'today' | 'forever') => void;
    markSeen: (id: string) => void;
  };
  isSuggesting: boolean;
  canSuggest: boolean;
  isSubmittingSuggestion: boolean;
  onEnterSuggest: () => void;
  onDiscardSuggest: () => void;
  onSubmitSuggest: (params: { title: string; description?: string }) => void | Promise<void>;
  forkLineage?: WorkflowForkLineage | null;
  isPreviewingHistory: boolean;
  isAuthoring: boolean;
  runState: RunState;
  lifecycleState: LifecycleState;
  runButtonStates: Record<RunState, LifecycleButtonStateConfig>;
  lifecycleButtonStates: Record<Exclude<LifecycleState, 'hidden'>, LifecycleButtonStateConfig>;
  isRunDisabled: boolean;
  isLifecycleDisabled: boolean;
  handleRunPress: () => void;
  handleLifecyclePress: () => void;
  handleRunWithConfig?: () => void;
  hasRunConfig?: boolean;
  /** When true, use the eligibility model (Pause/Unpause); otherwise the promotion model. */
  eligibilityModel: boolean;
  /** Manual pause state (derived from `pausedAt`). Toggles the Pause/Unpause menu item (flag ON). */
  workflowPaused: boolean;
  handlePause: () => void;
  handleUnpause: () => void;
  /** Promotion model (flag OFF): approval + promotion axes driving Force Promote / Pause. */
  workflowIsApproved: boolean;
  workflowPromotion: 'promoted' | 'paused' | null;
  setForcePromoteDialogOpen: (v: boolean) => void;
  /** Opens the member-visible flow optimization confirmation dialog. */
  onOptimizePress?: () => void;
  /** True while a convergence run for this flow is queued/running. */
  isOptimizePending?: boolean;
  /** Explicit manual pin timestamp (raw `cachePinnedAt` column), null when never manually pinned. */
  cachePinnedAt: string | null;
  /** Derived: explicit pin OR the org's auto-maintain. Never true for a shadow convergence fork. */
  effectivePinned: boolean;
  /** True for a shadow convergence fork — cache pinning is never offered on a shadow fork. */
  isShadowFork: boolean;
  /** Pins (true) or unpins (false) the workflow's cache. */
  onPinToggle: (pin: boolean) => void;
  /** True while a pin/unpin mutation is in flight. */
  isPinMutationPending: boolean;
};

/**
 * Two-line menu item content: leading colored icon + bold label + muted
 * description paragraph. Used inside `SplitButton.Item` to disambiguate
 * run-flow options inline, so users don't need a tooltip to understand each
 * choice.
 */
const RunMenuItemContent: React.FC<{
  label: string;
  description: string;
  icon?: React.ReactNode;
}> = ({ label, description, icon }) => (
  <span className="flex w-full items-start gap-2">
    {icon && <span className="mt-0.5 flex-shrink-0">{icon}</span>}
    <span className="flex flex-1 flex-col gap-0.5">
      <span className="font-medium leading-tight">{label}</span>
      <span className="text-xs text-muted-foreground leading-snug">{description}</span>
    </span>
  </span>
);

/**
 * Width override for run-menu items so the descriptions read naturally on
 * one or two lines and the popover sizes itself to fit. SplitButton's base
 * item is `flex items-center`; we drop to vertical layout via flex-col.
 */
const RUN_MENU_ITEM_CLASS = 'flex-col !items-start w-80 py-2';

export const FlowDesignerToolbar: React.FC<FlowDesignerToolbarProps> = ({
  workflowId,
  workflowCanvasTitle,
  workflowFlowType,
  generatedName,
  typingComplete,
  isWorkflowLoading,
  isSettingsOpen,
  setIsSettingsOpen,
  sandboxControl,
  sandboxV2Control,
  workflowHealth,
  onReEnable,
  isReEnabling,
  hintData,
  isSuggesting,
  canSuggest,
  isSubmittingSuggestion,
  onEnterSuggest,
  onDiscardSuggest,
  onSubmitSuggest,
  forkLineage,
  isPreviewingHistory,
  isAuthoring,
  runState,
  lifecycleState,
  runButtonStates,
  lifecycleButtonStates,
  isRunDisabled,
  isLifecycleDisabled,
  handleRunPress,
  handleLifecyclePress,
  handleRunWithConfig,
  hasRunConfig,
  eligibilityModel,
  workflowPaused,
  handlePause,
  handleUnpause,
  workflowIsApproved,
  workflowPromotion,
  setForcePromoteDialogOpen,
  onOptimizePress,
  isOptimizePending,
  cachePinnedAt,
  effectivePinned,
  isShadowFork,
  onPinToggle,
  isPinMutationPending,
}) => {
  const [isMergeOpen, setMergeOpen] = React.useState(false);
  const forkAction = forkLineage ? getForkLineageAction(forkLineage) : null;

  return (
  <div
    role="toolbar"
    aria-label="Flow actions"
    className={
      '@container absolute top-0 left-0 right-0 z-20 flex items-center gap-4 px-4 py-3 backdrop-blur transition-colors ' +
      (isSuggesting
        ? 'bg-primary/5 supports-[backdrop-filter]:bg-primary/5 border-b-2 border-primary'
        : 'bg-background/95 supports-[backdrop-filter]:bg-background/60 border-b border-border')
    }
  >
    <div className="flex items-center gap-2 flex-1 min-w-0">
      {workflowId && !isSettingsOpen && (
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Settings"
          onPress={() => setIsSettingsOpen(true)}
          aria-label="Workflow settings"
        >
          <Gear size={16} />
        </Button>
      )}

      {sandboxControl && !isSettingsOpen && (
        <SandboxToolbarButton
          environmentName={sandboxControl.environmentName}
          sandbox={sandboxControl.sandbox}
          isEnsuring={sandboxControl.isEnsuring}
          isStopping={sandboxControl.isStopping}
          onStart={sandboxControl.onStart}
          onStop={sandboxControl.onStop}
        />
      )}

      {sandboxV2Control && !isSettingsOpen && (
        <SandboxV2ToolbarButton
          environmentName={sandboxV2Control.environmentName}
          environment={sandboxV2Control.environment}
          isLoading={sandboxV2Control.isLoading}
          status={sandboxV2Control.status}
          version={sandboxV2Control.version}
          error={sandboxV2Control.error}
          currentRun={sandboxV2Control.currentRun}
        />
      )}

      {hintData.hints.length > 0 && !isSettingsOpen && (
        <Button
          variant="icon"
          size="sm"
          iconOnly
          tooltip="Tips"
          onPress={hintData.isOpen ? hintData.close : hintData.open}
          aria-label="Tips"
        >
          <Lightbulb size={16} weight={hintData.hasUnread ? 'fill' : 'regular'} />
        </Button>
      )}

      {workflowHealth === 'degraded' && onReEnable && !isSettingsOpen && (
        <DegradedHealthMenu onReEnable={onReEnable} isReEnabling={isReEnabling} />
      )}

      {!isSettingsOpen &&
        (isSuggesting ? (
          <SuggestModeIndicator />
        ) : (
          <SuggestModeToggle canEnter={canSuggest} onEnter={onEnterSuggest} />
        ))}

      {workflowId && workflowCanvasTitle && workflowCanvasTitle !== DEFAULT_WORKFLOW_NAME && (
        <Flex align="center" gap="xs" className="min-w-0 flex-1 ml-2 overflow-hidden">
          {workflowFlowType === 'setup' && (
            <TagBadge name="Setup" color="gray" hideDot className="text-[10px] flex-shrink-0" />
          )}
          {workflowFlowType === 'seed' && (
            <TagBadge name="seed" color="green" size="sm" className="flex-shrink-0" />
          )}
          {effectivePinned && (
            <TooltipTrigger delay={0}>
              <span className="hidden flex-shrink-0 @xl:inline-flex">
                <TagBadge
                  name="Pinned"
                  color="blue"
                  size="sm"
                  icon={<PushPin size={11} weight="bold" />}
                  tooltip={null}
                />
              </span>
              <Tooltip variant="light" className="max-w-xs px-2 py-1 text-xs">
                {cachePinnedAt
                  ? `Pinned ${new Date(cachePinnedAt).toLocaleDateString()}`
                  : 'Pinned by auto-maintain — disable auto-maintain in org settings to unpin'}
              </Tooltip>
            </TooltipTrigger>
          )}
          <Text
            weight="semibold"
            title={workflowCanvasTitle}
            className="min-w-0 flex-shrink block text-base truncate"
          >
            {workflowCanvasTitle}
          </Text>
          {generatedName && !typingComplete && !isWorkflowLoading && (
            <span className="inline-block w-0.5 h-4 bg-primary animate-pulse flex-shrink-0" />
          )}
          {forkLineage && (
            <div className="hidden min-w-0 @2xl:flex">
              <ForkOriginLink lineage={forkLineage} />
            </div>
          )}
        </Flex>
      )}
    </div>

    <div className="flex items-center gap-2 flex-shrink-0">
      {isSuggesting && (
        <SuggestModeActions
          isSubmitting={isSubmittingSuggestion}
          onDiscard={onDiscardSuggest}
          onSubmit={onSubmitSuggest}
        />
      )}
      {!isSuggesting && !isPreviewingHistory && !isAuthoring && (
        <div className="flex items-center gap-2">
          {forkLineage && (
            <MenuTrigger>
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="Original flow"
                aria-label="Original flow actions"
              >
                <GitFork size={16} />
              </Button>
              <Menu
                onAction={(key) => {
                  if (key === 'open') forkLineage.onOpenOrigin();
                  else if (key === 'merge') setMergeOpen(true);
                  else if (key === 'suggest') forkLineage.onSuggestToOriginal();
                }}
              >
                <MenuItem id="open">{`Open ${forkLineage.originName ?? 'original'}`}</MenuItem>
                {forkAction === 'suggest' && (
                  <MenuItem id="suggest" isDisabled={forkLineage.isSuggestingToOriginal}>
                    {forkLineage.isSuggestingToOriginal
                      ? 'Suggesting…'
                      : 'Suggest changes to original'}
                  </MenuItem>
                )}
                {forkAction === 'merge' && (
                  <MenuItem id="merge" isDisabled={isForkMergeDisabled(forkLineage)}>
                    {forkLineage.isMergingToOriginal ? 'Merging…' : 'Merge into original'}
                  </MenuItem>
                )}
              </Menu>
            </MenuTrigger>
          )}
          {lifecycleState !== 'hidden' && (
            <MorphButton
              size="sm"
              state={lifecycleState}
              states={lifecycleButtonStates}
              onPress={handleLifecyclePress}
              isDisabled={isLifecycleDisabled}
              className="min-w-[150px]"
            />
          )}

          {runState === 'idle' ? (
            <SplitButton
              size="sm"
              variant="primary"
              iconLeft={runButtonStates.idle.icon}
              onPress={handleRunPress}
              isDisabled={isRunDisabled}
              className="min-w-[150px]"
            >
              {runButtonStates.idle.label}
              {hasRunConfig && handleRunWithConfig && (
                <SplitButton.Item className={RUN_MENU_ITEM_CLASS} onAction={handleRunWithConfig}>
                  <RunMenuItemContent
                    icon={<Sliders size={16} weight="bold" className="text-blue-500" />}
                    label="Run with config"
                    description="Override the viewport, inputs, scenario, or environment before running."
                  />
                </SplitButton.Item>
              )}
              {onOptimizePress && workflowId && (
                <SplitButton.Item
                  className={RUN_MENU_ITEM_CLASS}
                  onAction={onOptimizePress}
                  isDisabled={isOptimizePending}
                >
                  <RunMenuItemContent
                    icon={<Sparkle size={16} weight="bold" className="text-violet-500" />}
                    label={isOptimizePending ? 'Optimizing…' : 'Optimize'}
                    description="Test safe cache improvements in a shadow copy and promote only proven changes."
                  />
                </SplitButton.Item>
              )}
              {!cachePinnedAt && !effectivePinned && !isShadowFork && workflowId && (
                <SplitButton.Item
                  className={RUN_MENU_ITEM_CLASS}
                  onAction={() => onPinToggle(true)}
                  isDisabled={isPinMutationPending}
                >
                  <RunMenuItemContent
                    icon={<PushPin size={16} weight="bold" className="text-blue-500" />}
                    label="Pin cache"
                    description="Keep replaying this flow's cache instead of drifting back to the agent."
                  />
                </SplitButton.Item>
              )}
              {cachePinnedAt && workflowId && (
                <SplitButton.Item
                  className={RUN_MENU_ITEM_CLASS}
                  onAction={() => onPinToggle(false)}
                  isDisabled={isPinMutationPending}
                >
                  <RunMenuItemContent
                    icon={<PushPinSlash size={16} weight="bold" className="text-muted-foreground" />}
                    label="Unpin cache"
                    description="Let the runtime and maintenance agent manage this flow's cache again."
                  />
                </SplitButton.Item>
              )}
              {/* Eligibility model (flag ON): standalone Pause/Unpause toggle. */}
              {eligibilityModel && workflowPaused && (
                <SplitButton.Item className={RUN_MENU_ITEM_CLASS} onAction={handleUnpause}>
                  <RunMenuItemContent
                    icon={<PlayCircle size={16} weight="bold" className="text-green-600" />}
                    label="Unpause Flow"
                    description="Resume including this flow in suite runs (when it's eligible)."
                  />
                </SplitButton.Item>
              )}
              {eligibilityModel && !workflowPaused && (
                <SplitButton.Item className={RUN_MENU_ITEM_CLASS} onAction={handlePause}>
                  <RunMenuItemContent
                    icon={<PauseCircle size={16} weight="bold" className="text-amber-500" />}
                    label="Pause Flow"
                    description="Exclude this flow from suite runs until it's unpaused."
                  />
                </SplitButton.Item>
              )}
              {/* Promotion model (flag OFF): Force Promote + promotion Pause. */}
              {!eligibilityModel && workflowIsApproved && workflowPromotion !== 'promoted' && (
                <SplitButton.Item
                  className={RUN_MENU_ITEM_CLASS}
                  onAction={() => setForcePromoteDialogOpen(true)}
                >
                  <RunMenuItemContent
                    icon={<ArrowCircleUp size={16} weight="bold" className="text-indigo-500" />}
                    label="Force Promote"
                    description="Include in suite runs even if the latest run hasn't succeeded."
                  />
                </SplitButton.Item>
              )}
              {!eligibilityModel && workflowPromotion === 'promoted' && (
                <SplitButton.Item className={RUN_MENU_ITEM_CLASS} onAction={handlePause}>
                  <RunMenuItemContent
                    icon={<PauseCircle size={16} weight="bold" className="text-amber-500" />}
                    label="Pause Flow"
                    description="Exclude from suite runs until re-promoted."
                  />
                </SplitButton.Item>
              )}
            </SplitButton>
          ) : (
            <MorphButton
              size="sm"
              state={runState}
              states={runButtonStates}
              onPress={handleRunPress}
              isDisabled={isRunDisabled}
              className="min-w-[150px]"
            />
          )}
        </div>
      )}
    </div>
    {forkLineage && (
      <ForkMergeDialog lineage={forkLineage} isOpen={isMergeOpen} onOpenChange={setMergeOpen} />
    )}
  </div>
  );
};
